// components/VideoPlayer.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Card, Button, Space, Spin } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined, DownloadOutlined, FullscreenOutlined, ShareAltOutlined } from '@ant-design/icons';

interface VideoPlayerProps {
  videoUrl: string;
  title: string;
  createdAt?: any;
  jobId: string;
  hideTitle?: boolean;
  onShare?: () => void;
}

const LOAD_TIMEOUT_MS = 10000;
const MAX_RETRIES = 2;

const VideoPlayer: React.FC<VideoPlayerProps> = ({ videoUrl, title, createdAt, jobId, hideTitle = false, onShare }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showThumbnail, setShowThumbnail] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [loadFailed, setLoadFailed] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const retryCountRef = useRef(0);
  const loadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearLoadTimer = () => {
    if (loadTimerRef.current) {
      clearTimeout(loadTimerRef.current);
      loadTimerRef.current = null;
    }
  };

  const scheduleLoadTimeout = (reason: string) => {
    clearLoadTimer();
    loadTimerRef.current = setTimeout(() => {
      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current += 1;
        console.warn(`[VideoPlayer] ${reason} — retry ${retryCountRef.current}/${MAX_RETRIES} for job ${jobId}`);
        if (videoRef.current) {
          videoRef.current.load();
        }
        scheduleLoadTimeout('timeout after retry');
      } else {
        console.error(`[VideoPlayer] Video load failed after ${MAX_RETRIES + 1} attempts for job ${jobId} (${videoUrl})`);
        setLoadFailed(true);
        setIsLoading(false);
      }
    }, LOAD_TIMEOUT_MS);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    retryCountRef.current = 0;
    setLoadFailed(false);
    setIsLoading(true);
    scheduleLoadTimeout('initial load timeout');
    return clearLoadTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
        setShowThumbnail(false);
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoLoad = () => {
    clearLoadTimer();
    setIsLoading(false);
    setLoadFailed(false);
    // Generate thumbnail from first frame
    if (videoRef.current && showThumbnail) {
      videoRef.current.currentTime = 0.1; // Seek to first frame
    }
  };

  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const mediaError = e.currentTarget.error;
    const errCode = mediaError?.code;
    const errMsg = mediaError?.message;
    if (retryCountRef.current < MAX_RETRIES) {
      retryCountRef.current += 1;
      console.warn(`[VideoPlayer] video error (code=${errCode} msg=${errMsg}) — retry ${retryCountRef.current}/${MAX_RETRIES} for job ${jobId}`);
      scheduleLoadTimeout('error retry timeout');
      if (videoRef.current) {
        videoRef.current.load();
      }
    } else {
      console.error(`[VideoPlayer] Video load failed after ${MAX_RETRIES + 1} attempts for job ${jobId} (${videoUrl}) — code=${errCode} msg=${errMsg}`);
      clearLoadTimer();
      setLoadFailed(true);
      setIsLoading(false);
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = videoUrl;
    link.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_video.mp4`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <Card
      title={!hideTitle ? title : null}
      extra={
        <Space size={isMobile ? 'small' : 'middle'}>
          <Button
            icon={<FullscreenOutlined />}
            onClick={handleFullscreen}
            disabled={!videoRef.current}
            size={isMobile ? 'small' : 'middle'}
          >
            {!isMobile && 'Fullscreen'}
          </Button>
          {onShare && (
            <Button
              type="primary"
              icon={<ShareAltOutlined />}
              onClick={onShare}
              size={isMobile ? 'small' : 'middle'}
            >
              Share Review
            </Button>
          )}
        </Space>
      }
      style={{ marginBottom: isMobile ? 12 : 24 }}
      bodyStyle={{ padding: isMobile ? '8px' : '24px' }}
    >
      <div style={{ 
        position: 'relative', 
        width: '100%', 
        backgroundColor: '#000', 
        borderRadius: isMobile ? 4 : 8, 
        overflow: 'hidden' 
      }}>
        {isLoading && !loadFailed && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10
          }}>
            <Spin size="large" />
          </div>
        )}

        {loadFailed && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
            color: '#fff',
            textAlign: 'center',
            padding: 16,
            background: 'rgba(0,0,0,0.6)',
            borderRadius: 8
          }}>
            <div style={{ marginBottom: 8 }}>Video failed to load.</div>
            <Button
              size="small"
              onClick={() => {
                retryCountRef.current = 0;
                setLoadFailed(false);
                setIsLoading(true);
                scheduleLoadTimeout('manual retry timeout');
                if (videoRef.current) videoRef.current.load();
              }}
            >
              Retry
            </Button>
          </div>
        )}
        
        {/* Thumbnail Overlay */}
        {showThumbnail && !isLoading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 5
            }}
            onClick={handlePlayPause}
          >
            <PlayCircleOutlined style={{ fontSize: isMobile ? 48 : 64, color: '#fff' }} />
          </div>
        )}

        <video
          ref={videoRef}
          src={videoUrl}
          style={{ width: '100%', height: 'auto', display: 'block' }}
          controls={!showThumbnail}
          onLoadedData={handleVideoLoad}
          onError={handleVideoError}
          onPlay={() => {
            setIsPlaying(true);
            setShowThumbnail(false);
          }}
          onPause={() => setIsPlaying(false)}
          onEnded={() => {
            setIsPlaying(false);
            setShowThumbnail(true);
          }}
          preload="metadata"
        />
      </div>
      
      {createdAt && (
        <div style={{ 
          marginTop: isMobile ? 8 : 12, 
          color: '#666', 
          fontSize: isMobile ? 12 : 14 
        }}>
          Created: {formatDate(createdAt)}
        </div>
      )}
    </Card>
  );
};

export default VideoPlayer;